import type { DayWeather } from '@/components/maps/DaySummaryCard';
import type { OpenMeteoCurrentWeather, WeatherForecast } from '@/services/weather';

export const mapWeatherConditionToDayCondition = (condition: string): DayWeather['condition'] => {
  const normalized = (condition || '').toLowerCase();
  if (normalized.includes('snow')) return 'snowy';
  if (normalized.includes('thunder') || normalized.includes('storm')) return 'stormy';
  if (normalized.includes('rain') || normalized.includes('drizzle')) return 'rainy';
  if (normalized.includes('wind')) return 'windy';
  if (normalized.includes('cloud') && normalized.includes('part')) return 'partly_cloudy';
  if (normalized.includes('cloud')) return 'cloudy';
  if (normalized.includes('clear') || normalized.includes('sun')) return 'sunny';
  return 'partly_cloudy';
};

export const weatherForecastToDayWeatherMap = (forecast: WeatherForecast | null | undefined): Record<string, DayWeather> => {
  const days: Record<string, DayWeather> = {};
  forecast?.forecast?.forEach((day) => {
    const key = (day.date || '').slice(0, 10);
    if (!key) return;
    days[key] = {
      condition: mapWeatherConditionToDayCondition(day.condition),
      tempHigh: Math.round(day.temperature.max),
      tempLow: Math.round(day.temperature.min),
      icon: day.icon,
    };
  });
  return days;
};

export const mergeCurrentWeatherIntoDay = (
  dayWeather: DayWeather | null | undefined,
  forecast: WeatherForecast | null | undefined,
  liveCurrent?: OpenMeteoCurrentWeather | null,
): DayWeather | null => {
  const liveTemp =
    typeof liveCurrent?.temperature === 'number' ? Math.round(liveCurrent.temperature) : null;
  const liveCondition = liveCurrent
    ? mapWeatherConditionToDayCondition(liveCurrent.condition)
    : null;
  const forecastCurrentTemp =
    typeof forecast?.current?.temperature === 'number'
      ? Math.round(forecast.current.temperature)
      : null;
  const forecastCondition = forecast?.current
    ? mapWeatherConditionToDayCondition(forecast.current.condition)
    : null;

  const currentTemp = liveTemp ?? forecastCurrentTemp;
  const currentCondition = liveCondition ?? forecastCondition;

  if (currentTemp == null && !dayWeather) {
    return null;
  }

  if (!dayWeather) {
    if (currentTemp == null || !currentCondition) return null;
    return {
      condition: currentCondition,
      tempHigh: currentTemp,
      tempLow: currentTemp,
      tempCurrent: currentTemp,
      icon: forecast?.current?.icon,
    };
  }

  if (currentTemp == null) {
    return dayWeather;
  }

  return {
    ...dayWeather,
    tempCurrent: currentTemp,
    tempHigh: Math.max(dayWeather.tempHigh, currentTemp),
    tempLow: Math.min(dayWeather.tempLow, currentTemp),
    condition: liveCondition ?? dayWeather.condition ?? currentCondition ?? dayWeather.condition,
    icon: dayWeather.icon ?? forecast?.current?.icon,
  };
};
